import { useState, useEffect } from "react";
import { getCompanyData } from "../utils/generalUtil";
import { getCompanySubscription, SubscriptionInfo } from "../api/SubscriptionAPI";

export const useCompanySubscription = () => {
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyData = getCompanyData();
      const companyId = companyData?.companyId;
      
      if (!companyId) {
        setError("ID da empresa não encontrado");
        setLoading(false);
        return;
      }

      const data = await getCompanySubscription(Number(companyId));
      setSubscriptionInfo(data);
    } catch (err) {
      console.error("Erro ao buscar informações da assinatura:", err);
      setError("Erro ao carregar informações da assinatura");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  // Determinar o limite de quadras baseado no plano atual
  const getCurrentPlanLimit = (): number => {
    if (!subscriptionInfo?.subscription?.plan) {
      return 0; // Sem plano = sem quadras
    }

    const planName = subscriptionInfo.subscription.plan.name.toUpperCase();
    
    // Mapear nomes dos planos para limites
    switch (planName) {
      case "BASICO":
      case "BÁSICO":
        return 1;
      case "AVANCADO":
        return 3;
      case "PRO":
        return 8;
      default:
        // Se não reconhecer o nome, usar o MaxCourts do plano
        return subscriptionInfo.subscription.plan.maxCourts || 1;
    }
  };

  // Verificar se pode adicionar mais quadras
  const canAddMoreCourts = (currentCourtCount: number): boolean => {
    const limit = getCurrentPlanLimit();
    return currentCourtCount < limit;
  };

  // Obter texto explicativo baseado no plano atual
  const getPlanLimitText = (): string => {
    if (!subscriptionInfo?.subscription?.plan) {
      return "Você precisa de um plano ativo para adicionar quadras";
    }

    const planName = subscriptionInfo.subscription.plan.name;
    const limit = getCurrentPlanLimit();
    
    if (limit === 1) {
      return `O plano ${planName} permite adicionar apenas uma quadra`;
    } else {
      return `O plano ${planName} permite adicionar no máximo ${limit} quadras`;
    }
  };

  // Verificar se o plano atual é adequado para o número de quadras
  const isPlanAdequate = (currentCourtCount: number): boolean => {
    return subscriptionInfo?.isPlanOptimal ?? true;
  };

  return {
    subscriptionInfo,
    loading,
    error,
    fetchSubscriptionInfo,
    getCurrentPlanLimit,
    canAddMoreCourts,
    getPlanLimitText,
    isPlanAdequate,
  };
}; 